// src/pages/AIManagement.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  where, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc,
  setDoc,
  addDoc, 
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { toast } from 'react-hot-toast';
import { 
  PlayCircleIcon,
  DocumentTextIcon,
  DocumentIcon,
  ArrowLeftIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  BookmarkIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const AIManagement = () => {
  const { currentUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('run-report');
  const [loading, setLoading] = useState(false); // Start as false for run-report tab
  const [refreshing, setRefreshing] = useState(false);
  
  // Creed Statements State
  const [creedStatements, setCreedStatements] = useState('');
  const [savingCreed, setSavingCreed] = useState(false);
  const [runningReport, setRunningReport] = useState(false);
  const [reportProgress, setReportProgress] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [reportProgressData, setReportProgressData] = useState(null);
  const [progressInterval, setProgressInterval] = useState(null);

  // Prompt Management State
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [promptText, setPromptText] = useState('');
  const [promptOrder, setPromptOrder] = useState(0);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  // Reports State
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Content Track Selection State
  const [contentTracks, setContentTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState('all');
  const [loadingTracks, setLoadingTracks] = useState(false);

  useEffect(() => {
    if (isSuperAdmin && isSuperAdmin()) {
      loadData();
    } else {
      // If not super admin, don't try to load data
      setLoading(false);
    }
  }, [currentUser, activeTab, isSuperAdmin]);
  
  // Separate useEffect for showArchived to avoid reloading when switching tabs
  useEffect(() => {
    if (isSuperAdmin && isSuperAdmin() && activeTab === 'reports') {
      loadReports();
    }
  }, [showArchived]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'prompts') {
        await loadPrompts();
      } else if (activeTab === 'reports') {
        await loadReports();
      } else if (activeTab === 'creeds') {
        await loadCreedStatements();
      } else if (activeTab === 'run-report') {
        // Load content tracks for dropdown
        await loadContentTracks();
      } else {
        setLoading(false);
        setRefreshing(false);
        return;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadPrompts = async () => {
    try {
      const promptsRef = collection(db, 'theologicalPrompts');
      const q = query(promptsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const loadedPrompts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setPrompts(loadedPrompts);
      
      // Auto-select first prompt if none selected
      if (!selectedPrompt && loadedPrompts.length > 0) {
        const firstPrompt = loadedPrompts[0];
        setSelectedPrompt(firstPrompt);
        setEditingPrompt(firstPrompt);
        setPromptName(firstPrompt.name);
        setPromptDescription(firstPrompt.description || '');
        setPromptText(firstPrompt.prompt);
        setPromptOrder(firstPrompt.order);
        setShowPromptEditor(true);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      throw error;
    }
  };

  const loadReports = async () => {
    try {
      // Load all reports and filter in memory to handle retroactive reports
      // (reports without the 'archived' field default to not archived)
      const reportsRef = collection(db, 'theologicalReports');
      const q = query(reportsRef, orderBy('startedAt', 'desc'));
      
      const snapshot = await getDocs(q);
      let loadedReports = snapshot.docs.map(doc => {
        const data = doc.data();
        // Explicitly handle archived field - default to false if undefined or null
        // Use strict equality to ensure we only treat true as archived
        const archivedValue = data.archived === true;
        return {
          id: doc.id,
          ...data,
          archived: archivedValue,
        };
      });
      
      // Filter by archived status in memory - use strict equality
      // Make sure we're comparing boolean values correctly
      if (showArchived) {
        // Show only archived reports (strictly true)
        loadedReports = loadedReports.filter(report => Boolean(report.archived) === true);
      } else {
        // Show only non-archived reports (anything that's not strictly true, including undefined/null)
        loadedReports = loadedReports.filter(report => Boolean(report.archived) !== true);
      }
      
      console.log(`[loadReports] showArchived: ${showArchived}, total loaded: ${snapshot.docs.length}, filtered to ${loadedReports.length} reports`);
      setReports(loadedReports);
    } catch (error) {
      console.error('Error loading reports:', error);
      throw error;
    }
  };

  const handleToggleArchiveReport = async (reportId, currentlyArchived) => {
    try {
      // Ensure we're working with a boolean value
      const currentStatus = currentlyArchived === true;
      const newArchivedStatus = !currentStatus;
      
      console.log(`[handleToggleArchiveReport] reportId: ${reportId}, current: ${currentStatus}, new: ${newArchivedStatus}`);
      
      await updateDoc(doc(db, 'theologicalReports', reportId), {
        archived: newArchivedStatus,
      });
      
      // If viewing a report detail, update the selected report immediately
      if (selectedReport?.id === reportId) {
        setSelectedReport({
          ...selectedReport,
          archived: newArchivedStatus,
        });
      }
      
      // Reload reports to reflect the change
      await loadReports();
      
      toast.success(`Report ${newArchivedStatus ? 'archived' : 'unarchived'} successfully.`);
    } catch (error) {
      console.error('Error toggling archive status:', error);
      toast.error('Failed to update archive status. Please try again.');
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleRunReport = async () => {
    if (!currentUser) return;
    
    const trackName = selectedTrackId === 'all' 
      ? 'all content tracks' 
      : contentTracks.find(t => t.id === selectedTrackId)?.name || 'selected track';
    
    if (!window.confirm(
      `This will scan ${trackName === 'all content tracks' ? 'all' : 'the "' + trackName + '"'} contentTrack resources and check them for theological accuracy. This may take several minutes. Continue?`
    )) {
      return;
    }

    try {
      setRunningReport(true);
      setReportProgress('Starting theological check...');
      setShowProgressModal(true);
      setReportProgressData(null);
      
      const runTheologicalCheck = httpsCallable(functions, 'runTheologicalCheck');
      const trackIdParam = selectedTrackId === 'all' || !selectedTrackId ? null : selectedTrackId;
      console.log('Calling runTheologicalCheck with:', { 
        userId: currentUser.uid, 
        userName: currentUser.name || currentUser.email,
        trackId: trackIdParam 
      });
      
      // Show progress immediately while waiting for function to return
      setReportProgress('Starting theological check... This may take several minutes.');
      
      const result = await runTheologicalCheck({
        userId: currentUser.uid,
        userName: currentUser.name || currentUser.email,
        trackId: trackIdParam,
      });
      
      if (result.data?.reportId) {
        setCurrentReportId(result.data.reportId);
        setReportProgress(`Report ${result.data.reportId} created. Processing...`);
        
        // Start listening to report updates
        startProgressTracking(result.data.reportId);
      } else {
        throw new Error('No report ID returned');
      }
    } catch (error) {
      console.error('Error running report:', error);
      toast.error(error.message || 'Failed to run theological check. Please try again.');
      setRunningReport(false);
      setReportProgress('');
      setShowProgressModal(false);
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  };

  const startProgressTracking = (reportId) => {
    // Set up real-time listener for the report document
    const reportRef = doc(db, 'theologicalReports', reportId);
    
    let unsubscribeSnapshot = null;
    
    const unsubscribe = onSnapshot(reportRef, (snapshot) => {
      if (snapshot.exists()) {
        const reportData = snapshot.data();
        setReportProgressData(reportData);
        
        console.log('[Progress Tracking] Report update:', {
          status: reportData.status,
          checked: reportData.checkedResources,
          total: reportData.totalResources,
          failed: reportData.failedResources,
          resultsCount: reportData.contentPieceResults?.length || 0
        });
        
        // Update progress text
        if (reportData.status === 'running') {
          const total = reportData.totalResources || 0;
          const checked = reportData.checkedResources || 0;
          const failed = reportData.failedResources || 0;
          const progressText = `Processing: ${checked}/${total} resources checked (${failed} failed)`;
          setReportProgress(progressText);
          console.log('[Progress Tracking]', progressText);
          
          // Show latest completed resource if available
          const results = reportData.contentPieceResults || [];
          if (results.length > 0) {
            const latestResult = results[results.length - 1];
            console.log(`[Progress Tracking] Latest result: "${latestResult.resourceTitle}" - Score: ${latestResult.overallScore}/10`);
          }
          
          // Keep modal open - don't close it
          setRunningReport(true);
          setShowProgressModal(true);
        } else if (reportData.status === 'completed') {
          setReportProgress('Theological check completed!');
          setRunningReport(false);
          console.log('[Progress Tracking] Report completed!');
          // Auto-close modal after 3 seconds and navigate to reports
          setTimeout(() => {
            if (unsubscribeSnapshot) {
              unsubscribeSnapshot();
            }
            setShowProgressModal(false);
            setActiveTab('reports');
            loadReports();
            toast.success('Theological check completed!');
          }, 3000);
        } else if (reportData.status === 'failed') {
          setReportProgress(`Failed: ${reportData.error || 'Unknown error'}`);
          setRunningReport(false);
          console.error('[Progress Tracking] Report failed:', reportData.error);
          setTimeout(() => {
            if (unsubscribeSnapshot) {
              unsubscribeSnapshot();
            }
            setShowProgressModal(false);
          }, 5000);
        } else if (reportData.status === 'cancelled') {
          setReportProgress('Report cancelled by user');
          setRunningReport(false);
          console.log('[Progress Tracking] Report cancelled');
          setTimeout(() => {
            if (unsubscribeSnapshot) {
              unsubscribeSnapshot();
            }
            setShowProgressModal(false);
            loadReports();
            toast.success('Report cancelled successfully');
          }, 2000);
        }
      } else {
        console.warn('[Progress Tracking] Report document does not exist:', reportId);
        // Keep modal open and use polling as fallback
        startProgressPolling(reportId);
      }
    }, (error) => {
      console.error('[Progress Tracking] Error listening to report updates:', error);
      // Fallback to polling if real-time fails - don't close modal
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      startProgressPolling(reportId);
    });
    
    unsubscribeSnapshot = unsubscribe;
  };

  const startProgressPolling = (reportId) => {
    console.log('[Progress Polling] Starting polling for report:', reportId);
    // Fallback polling mechanism
    const interval = setInterval(async () => {
      try {
        const reportRef = doc(db, 'theologicalReports', reportId);
        const reportDoc = await getDoc(reportRef);
        
        if (reportDoc.exists()) {
          const reportData = reportDoc.data();
          setReportProgressData(reportData);
          
          console.log('[Progress Polling] Report status:', {
            status: reportData.status,
            checked: reportData.checkedResources,
            total: reportData.totalResources,
            failed: reportData.failedResources,
            resultsCount: reportData.contentPieceResults?.length || 0
          });
          
          if (reportData.status === 'running') {
            const total = reportData.totalResources || 0;
            const checked = reportData.checkedResources || 0;
            const failed = reportData.failedResources || 0;
            const progressText = `Processing: ${checked}/${total} resources checked (${failed} failed)`;
            setReportProgress(progressText);
            setRunningReport(true);
            setShowProgressModal(true); // Ensure modal stays open
            
            // Show latest completed resource
            const results = reportData.contentPieceResults || [];
            if (results.length > 0) {
              const latestResult = results[results.length - 1];
              console.log(`[Progress Polling] Latest: "${latestResult.resourceTitle}" - Score: ${latestResult.overallScore}/10`);
            }
          } else if (reportData.status === 'completed') {
            setReportProgress('Theological check completed!');
            setRunningReport(false);
            clearInterval(interval);
            setProgressInterval(null);
            console.log('[Progress Polling] Report completed!');
            setTimeout(() => {
              setShowProgressModal(false);
              setActiveTab('reports');
              loadReports();
              toast.success('Theological check completed!');
            }, 3000);
          } else if (reportData.status === 'failed') {
            setReportProgress(`Failed: ${reportData.error || 'Unknown error'}`);
            setRunningReport(false);
            clearInterval(interval);
            setProgressInterval(null);
            console.error('[Progress Polling] Report failed:', reportData.error);
            setTimeout(() => {
              setShowProgressModal(false);
            }, 5000);
          }
        } else {
          console.warn('[Progress Polling] Report document not found, continuing to poll...');
        }
      } catch (error) {
        console.error('[Progress Polling] Error polling report:', error);
      }
    }, 2000); // Poll every 2 seconds
    
    setProgressInterval(interval);
  };

  const handleCloseProgressModal = () => {
    if (window.confirm('Close progress modal? The check will continue running in the background. You can view the report later in the Reports tab.')) {
      setShowProgressModal(false);
      // Keep tracking in background, just hide modal
      // Don't clear progressInterval - let it keep running
    }
  };

  const handleCancelReport = async () => {
    if (!currentReportId) {
      toast.error('No report ID found');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this report? The function will stop processing after the current resource completes.')) {
      return;
    }

    try {
      const reportRef = doc(db, 'theologicalReports', currentReportId);
      await updateDoc(reportRef, {
        status: 'cancelled',
        error: 'Cancelled by user',
      });
      toast.success('Report cancellation requested. The function will stop after the current resource.');
      setReportProgress('Cancelling... The function will stop after the current resource.');
      console.log('[Cancel] Report marked as cancelled:', currentReportId);
    } catch (error) {
      console.error('Error cancelling report:', error);
      toast.error('Failed to cancel report. Please try again.');
    }
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt);
    setEditingPrompt(prompt);
    setPromptName(prompt.name);
    setPromptDescription(prompt.description || '');
    setPromptText(prompt.prompt);
    setPromptOrder(prompt.order);
    setShowPromptEditor(true);
  };

  const handleSavePrompt = async () => {
    if (!promptName.trim() || !promptText.trim()) {
      toast.error('Please provide a name and prompt text.');
      return;
    }

    try {
      if (editingPrompt) {
        // Update existing prompt
        await updateDoc(doc(db, 'theologicalPrompts', editingPrompt.id), {
          name: promptName.trim(),
          description: promptDescription.trim(),
          prompt: promptText.trim(),
          order: promptOrder,
          updatedAt: serverTimestamp(),
        });
        toast.success('Prompt updated successfully.');
      } else {
        // Create new prompt
        const newOrder = prompts.length > 0 
          ? Math.max(...prompts.map(p => p.order)) + 1 
          : 1;
        await addDoc(collection(db, 'theologicalPrompts'), {
          name: promptName.trim(),
          description: promptDescription.trim(),
          prompt: promptText.trim(),
          order: newOrder,
          enabled: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        toast.success('Prompt created successfully.');
      }

      setShowPromptEditor(false);
      setEditingPrompt(null);
      setPromptName('');
      setPromptDescription('');
      setPromptText('');
      setPromptOrder(0);
      loadPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save prompt. Please try again.');
    }
  };

  const handleCreateNewPrompt = () => {
    setEditingPrompt(null);
    setPromptName('');
    setPromptDescription('');
    setPromptText('');
    setPromptOrder(prompts.length > 0 ? Math.max(...prompts.map(p => p.order)) + 1 : 1);
    setShowPromptEditor(true);
  };

  const handleReorderPrompt = async (promptId, direction) => {
    const promptIndex = prompts.findIndex(p => p.id === promptId);
    if (promptIndex === -1) return;

    const newIndex = direction === 'up' ? promptIndex - 1 : promptIndex + 1;
    if (newIndex < 0 || newIndex >= prompts.length) return;

    const prompt = prompts[promptIndex];
    const otherPrompt = prompts[newIndex];

    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'theologicalPrompts', prompt.id), {
        order: otherPrompt.order,
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, 'theologicalPrompts', otherPrompt.id), {
        order: prompt.order,
        updatedAt: serverTimestamp(),
      });
      await batch.commit();

      loadPrompts();
      toast.success('Prompt order updated.');
    } catch (error) {
      console.error('Error reordering prompt:', error);
      toast.error('Failed to reorder prompt.');
    }
  };

  const handleTogglePromptEnabled = async (promptId, enabled) => {
    try {
      await updateDoc(doc(db, 'theologicalPrompts', promptId), {
        enabled: !enabled,
        updatedAt: serverTimestamp(),
      });
      loadPrompts();
      toast.success(`Prompt ${!enabled ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Error toggling prompt:', error);
      toast.error('Failed to toggle prompt.');
    }
  };

  const loadCreedStatements = async () => {
    try {
      const creedDocRef = doc(db, 'appSettings', 'creedStatements');
      const creedDoc = await getDoc(creedDocRef);
      
      if (creedDoc.exists()) {
        setCreedStatements(creedDoc.data().statements || '');
      } else {
        setCreedStatements('');
      }
    } catch (error) {
      console.error('Error loading creed statements:', error);
      throw error;
    }
  };

  const loadContentTracks = async () => {
    try {
      setLoadingTracks(true);
      
      const publishedQuery = query(
        collection(db, 'contentTracks'),
        where('status', '==', 'published')
      );
      const activeQuery = query(
        collection(db, 'contentTracks'),
        where('status', '==', 'active')
      );
      
      const [publishedSnap, activeSnap] = await Promise.all([
        getDocs(publishedQuery),
        getDocs(activeQuery),
      ]);
      
      const allDocs = [...publishedSnap.docs, ...activeSnap.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );
      
      const tracksSnapshot = { docs: uniqueDocs };

      const tracksData = tracksSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.data().title || 'Untitled Track',
        ...doc.data(),
      })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setContentTracks(tracksData);
    } catch (error) {
      console.error('Error loading content tracks:', error);
      toast.error('Failed to load content tracks');
    } finally {
      setLoadingTracks(false);
    }
  };

  const handleSaveCreed = async () => {
    if (!creedStatements.trim()) {
      toast.error('Please enter at least one creed statement.');
      return;
    }

    try {
      setSavingCreed(true);
      await setDoc(doc(db, 'appSettings', 'creedStatements'), {
        statements: creedStatements.trim(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      }, { merge: true });
      
      toast.success('Creed statements saved successfully.');
    } catch (error) {
      console.error('Error saving creed statements:', error);
      toast.error('Failed to save creed statements. Please try again.');
    } finally {
      setSavingCreed(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 5) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const renderRunReportTab = () => (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <PlayCircleIcon className="w-10 h-10 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Theological Protection System</h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          This system will scan contentTrack resources and check them for theological accuracy
          using AI-powered analysis. Each resource will be evaluated against multiple theological
          checkpoints and scored from 1-10 (10 = perfect, 1 = heretical).
        </p>
        
        {/* Content Track Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Content Track
          </label>
          <select
            value={selectedTrackId}
            onChange={(e) => setSelectedTrackId(e.target.value)}
            disabled={loadingTracks || runningReport}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="all">All Content Tracks</option>
            {contentTracks.map((track) => (
              <option key={track.id} value={track.id}>
                {track.name || 'Untitled Track'}
              </option>
            ))}
          </select>
          {loadingTracks && (
            <p className="mt-2 text-sm text-gray-500">Loading tracks...</p>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> All configured creed statements from the "Creed Statements" tab will be automatically included 
            as foundational truth when running theological checks. Prompts will reference these creeds when evaluating content.
          </p>
        </div>
        
        <button
          onClick={handleRunReport}
          disabled={runningReport || loadingTracks}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlayCircleIcon className="w-6 h-6 mr-2" />
          Run Theological Check
        </button>
      </div>

      {/* Progress Modal */}
      {showProgressModal && renderProgressModal()}
    </div>
  );

  const renderProgressModal = () => {
    const progressData = reportProgressData;
    const total = progressData?.totalResources || 0;
    const checked = progressData?.checkedResources || 0;
    const failed = progressData?.failedResources || 0;
    const completed = progressData?.status === 'completed';
    const failedStatus = progressData?.status === 'failed';
    const cancelledStatus = progressData?.status === 'cancelled';
    const contentResults = progressData?.contentPieceResults || [];

    const progressPercentage = total > 0 ? Math.round((checked / total) * 100) : 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              {runningReport && !completed && !failedStatus && !cancelledStatus && (
                <ArrowPathIcon className="w-6 h-6 text-blue-600 animate-spin mr-3" />
              )}
              {completed && (
                <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3" />
              )}
              {failedStatus && (
                <XCircleIcon className="w-6 h-6 text-red-600 mr-3" />
              )}
              {cancelledStatus && (
                <XCircleIcon className="w-6 h-6 text-orange-600 mr-3" />
              )}
              <h2 className="text-2xl font-bold text-gray-800">Theological Check Progress</h2>
            </div>
            <div className="flex items-center gap-2">
              {runningReport && !completed && !failedStatus && !cancelledStatus && (
                <button
                  onClick={handleCancelReport}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
                  title="Cancel Report"
                >
                  <XCircleIcon className="w-4 h-4" />
                  Cancel
                </button>
              )}
              <button
                onClick={handleCloseProgressModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={false}
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                {completed ? 'Completed' : failedStatus ? 'Failed' : cancelledStatus ? 'Cancelled' : 'Processing...'}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {checked} / {total} resources ({progressPercentage}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${
                  completed ? 'bg-green-500' : failedStatus ? 'bg-red-500' : cancelledStatus ? 'bg-orange-500' : 'bg-blue-600'
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            {reportProgress && (
              <p className="text-sm text-gray-600 mt-3">{reportProgress}</p>
            )}
            {failedStatus && progressData?.error && (
              <p className="text-sm text-red-600 mt-2">Error: {progressData.error}</p>
            )}
          </div>

          {/* Content List */}
          <div className="flex-1 overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Content Pieces Processed ({contentResults.length})
            </h3>
            
            {contentResults.length === 0 && (
              <div className="text-center py-8">
                <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-gray-500">Waiting for first results...</p>
              </div>
            )}

            <div className="space-y-3">
              {contentResults.map((content, index) => {
                const isLatest = index === contentResults.length - 1 && runningReport;
                return (
                  <div
                    key={content.resourceId || index}
                    className={`border rounded-lg p-4 transition-colors ${
                      isLatest 
                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        {isLatest && (
                          <span className="inline-block px-2 py-1 mb-1 text-xs font-semibold bg-blue-600 text-white rounded">
                            Processing...
                          </span>
                        )}
                        <h4 className="font-semibold text-gray-800">
                          {content.resourceTitle || `Resource ${content.resourceId || index + 1}`}
                        </h4>
                        {/* Show gatekeeper/spiritual safety status */}
                        {content.isDoctrinal === false && (
                          <span className="inline-block px-2 py-1 mt-1 text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300 rounded">
                            Non-Doctrinal Content
                          </span>
                        )}
                        {content.skippedDoctrinalChecks && (
                          <span className="inline-block px-2 py-1 mt-1 ml-2 text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300 rounded">
                            Doctrinal Checks Skipped
                          </span>
                        )}
                        {content.isDoctrinal === true && !content.skippedDoctrinalChecks && (
                          <span className="inline-block px-2 py-1 mt-1 text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300 rounded">
                            Doctrinal Content
                          </span>
                        )}
                      </div>
                      {content.overallScore !== undefined && !isLatest && (
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            content.overallScore >= 8
                              ? 'bg-green-100 text-green-800'
                              : content.overallScore >= 5
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {content.overallScore}/10
                        </span>
                      )}
                    </div>
                  
                    {content.status === 'failed' && (
                      <p className="text-sm text-red-600 mb-2">Failed: {content.error || 'Unknown error'}</p>
                    )}

                    {content.checks && content.checks.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {content.checks.map((check, checkIndex) => (
                          <div key={checkIndex} className="text-sm text-gray-600 flex items-start">
                            {check.checksOut ? (
                              <span className="inline-block px-2 py-1 rounded text-xs font-semibold mr-2 bg-green-100 text-green-800 border border-green-500 flex items-center gap-1">
                                <CheckCircleIcon className="w-3 h-3" />
                                Checks Out
                              </span>
                            ) : (
                              <span
                                className={`inline-block w-12 text-center px-2 py-1 rounded text-xs font-semibold mr-2 ${
                                  check.score >= 8
                                    ? 'bg-green-100 text-green-800'
                                    : check.score >= 5
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {check.score}/10
                              </span>
                            )}
                            <div className="flex-1">
                              <span className="font-medium text-gray-700">{check.promptName}:</span>{' '}
                              <span className="text-gray-600">{check.analysis}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {runningReport && contentResults.length > 0 && (
              <div className="mt-4 text-center">
                <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin inline-block mr-2" />
                <span className="text-sm text-gray-600">Processing more content...</span>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-6 border-t border-gray-200 flex justify-end">
            {completed || failedStatus ? (
              <button
                onClick={() => {
                  setShowProgressModal(false);
                  if (completed) {
                    setActiveTab('reports');
                    loadReports();
                  }
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {completed ? 'View Full Report' : 'Close'}
              </button>
            ) : (
              <button
                onClick={handleCloseProgressModal}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close (Continue in Background)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPromptsTab = () => (
    <div className="flex gap-6 h-[calc(100vh-300px)]">
      {/* Left Sidebar - Prompt List */}
      <div className="w-80 bg-white rounded-lg shadow-lg p-4 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Theological Prompts</h3>
          <button
            onClick={handleCreateNewPrompt}
            className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            New
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto space-y-2">
          {prompts.map((prompt) => (
            <div
              key={prompt.id}
              onClick={() => handlePromptSelect(prompt)}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPrompt?.id === prompt.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {prompt.enabled ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-xs font-semibold text-gray-500">#{prompt.order}</span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReorderPrompt(prompt.id, 'up');
                    }}
                    disabled={prompt.order === 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReorderPrompt(prompt.id, 'down');
                    }}
                    disabled={prompt.order === prompts.length}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">{prompt.name}</h4>
              {prompt.description && (
                <p className="text-sm text-gray-600 line-clamp-2">{prompt.description}</p>
              )}
            </div>
          ))}
          {prompts.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No prompts yet</p>
              <p className="text-sm text-gray-500 mt-1">Click "New" to create your first prompt</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Prompt Editor */}
      <div className="flex-1 bg-white rounded-lg shadow-lg p-6 flex flex-col">
        {showPromptEditor ? (
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {editingPrompt ? 'Edit Prompt' : 'New Prompt'}
              </h3>
              <button
                onClick={() => {
                  setShowPromptEditor(false);
                  setEditingPrompt(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prompt Name *
                </label>
                <input
                  type="text"
                  value={promptName}
                  onChange={(e) => setPromptName(e.target.value)}
                  placeholder="e.g., Trinitarian Doctrine Check"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={promptDescription}
                  onChange={(e) => setPromptDescription(e.target.value)}
                  placeholder="Brief description of what this prompt checks"
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Prompt Text *
                </label>
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder="Enter the prompt that will be sent to ChatGPT..."
                  rows="8"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={promptOrder}
                  onChange={(e) => setPromptOrder(parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <button
                onClick={handleSavePrompt}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Save Prompt
              </button>
              {editingPrompt && (
                <button
                  onClick={() => handleTogglePromptEnabled(editingPrompt.id, editingPrompt.enabled)}
                  className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium flex items-center justify-center"
                >
                  {editingPrompt.enabled ? (
                    <>
                      <EyeSlashIcon className="w-5 h-5 mr-2" />
                      Disable Prompt
                    </>
                  ) : (
                    <>
                      <EyeIcon className="w-5 h-5 mr-2" />
                      Enable Prompt
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Select a prompt to edit</h3>
            <p className="text-gray-600 max-w-md">
              Choose a prompt from the list on the left, or create a new one
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderReportsTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-800">Theological Reports</h3>
        <button
          onClick={async () => {
            const newValue = !showArchived;
            console.log(`[Toggle Archive] Changing showArchived from ${showArchived} to ${newValue}`);
            setShowArchived(newValue);
            // loadReports will be called by useEffect when showArchived changes
          }}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
            showArchived
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-600 text-white hover:bg-gray-700'
          }`}
        >
          {showArchived ? (
            <>
              <ArchiveBoxArrowDownIcon className="w-4 h-4" />
              Show Active
            </>
          ) : (
            <>
              <ArchiveBoxIcon className="w-4 h-4" />
              Show Archived
            </>
          )}
        </button>
      </div>

      {selectedReport ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <button
            onClick={() => setSelectedReport(null)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Reports
          </button>

          <div className="mb-6 pb-4 border-b">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Report #{selectedReport.id.slice(-8)}
            </h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p>Started: {selectedReport.startedAt ? format(selectedReport.startedAt?.toDate ? selectedReport.startedAt.toDate() : new Date(selectedReport.startedAt.seconds * 1000), 'PPpp') : 'N/A'}</p>
              {selectedReport.completedAt && (
                <p>Completed: {format(selectedReport.completedAt?.toDate ? selectedReport.completedAt.toDate() : new Date(selectedReport.completedAt.seconds * 1000), 'PPpp')}</p>
              )}
              <p>Status: <span className="font-semibold">{selectedReport.status}</span></p>
              <p>Resources Checked: {selectedReport.checkedResources} / {selectedReport.totalResources}</p>
            </div>
            <button
              onClick={() => handleToggleArchiveReport(selectedReport.id, selectedReport.archived === true)}
              className={`mt-4 inline-flex items-center px-4 py-2 rounded-lg font-medium ${
                selectedReport.archived === true
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {selectedReport.archived === true ? (
                <>
                  <ArchiveBoxArrowDownIcon className="w-5 h-5 mr-2" />
                  Unarchive Report
                </>
              ) : (
                <>
                  <ArchiveBoxIcon className="w-5 h-5 mr-2" />
                  Archive Report
                </>
              )}
            </button>
          </div>

          <div className="space-y-6">
            {selectedReport.contentPieceResults?.map((result, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {result.resourceTitle || `Content Piece ${index + 1}`}
                </h3>
                <div className="space-y-3">
                  {result.checks?.map((check, checkIndex) => (
                    <div key={checkIndex} className={`bg-gray-50 rounded-lg p-4 border-l-4 ${check.checksOut ? 'border-green-500' : 'border-blue-500'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-gray-800">{check.promptName}</span>
                        {check.checksOut ? (
                          <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-500 font-semibold text-sm flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            Checks Out
                          </span>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-white font-bold ${getScoreColor(check.score)}`}>
                            {check.score}/10
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{check.analysis}</p>
                    </div>
                  ))}
                  {result.overallScore !== undefined && (
                    <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                      <span className="font-semibold text-gray-800">Overall Score:</span>
                      <span className={`text-xl font-bold ${getScoreColor(result.overallScore).replace('bg-', 'text-')}`}>
                        {result.overallScore.toFixed(1)}/10
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-white rounded-lg shadow-lg p-4 hover:shadow-xl transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 flex-1">
                  <div 
                    onClick={() => setSelectedReport(report)}
                    className="cursor-pointer flex-1"
                  >
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-semibold text-gray-800">
                        Report #{report.id.slice(-8)}
                      </h4>
                      {report.archived === true && (
                        <span className="px-2 py-1 bg-gray-500 text-white text-xs font-semibold rounded flex items-center gap-1">
                          <ArchiveBoxIcon className="w-3 h-3" />
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleArchiveReport(report.id, report.archived === true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title={report.archived === true ? 'Unarchive' : 'Archive'}
                  >
                    {report.archived === true ? (
                      <ArchiveBoxArrowDownIcon className="w-5 h-5" />
                    ) : (
                      <ArchiveBoxIcon className="w-5 h-5" />
                    )}
                  </button>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                    report.status === 'completed' ? 'bg-green-500' : 
                    report.status === 'running' ? 'bg-yellow-500' : 
                    'bg-red-500'
                  }`}>
                    {report.status.toUpperCase()}
                  </span>
                </div>
              </div>
              <div 
                onClick={() => setSelectedReport(report)}
                className="cursor-pointer"
              >
                <p className="text-sm text-gray-600 mb-1">
                  {report.startedAt ? format(report.startedAt?.toDate ? report.startedAt.toDate() : new Date(report.startedAt.seconds * 1000), 'PPpp') : 'N/A'}
                </p>
                <p className="text-sm text-gray-700">
                  {report.checkedResources} / {report.totalResources} resources checked
                </p>
                {report.error && (
                  <p className="text-xs text-red-600 mt-2 italic">Error: {report.error}</p>
                )}
              </div>
            </div>
          ))}
          {reports.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-lg">
              <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No reports yet</p>
              <p className="text-sm text-gray-500 mt-1">Run a theological check to generate your first report</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCreedStatementsTab = () => (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Creed Statements</h2>
          <p className="text-gray-600">
            Enter all foundational creed statements below. These will be automatically included 
            in all theological check prompts as reference for orthodoxy.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            All Creed Statements
          </label>
          <textarea
            value={creedStatements}
            onChange={(e) => setCreedStatements(e.target.value)}
            placeholder="Enter all creed statements here...&#10;&#10;Example:&#10;&#10;Apostles' Creed:&#10;I believe in God, the Father almighty, creator of heaven and earth...&#10;&#10;Nicene Creed:&#10;We believe in one God, the Father, the Almighty, maker of heaven and earth...&#10;&#10;You can include multiple creeds separated by blank lines or sections."
            rows="20"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            style={{ minHeight: '500px' }}
          />
          <p className="text-xs text-gray-500 mt-2">
            All text entered here will be included at the beginning of each theological check prompt 
            to provide foundational theological truth for reference.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSaveCreed}
            disabled={savingCreed}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {savingCreed ? (
              <>
                <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Creed Statements'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (!isSuperAdmin || !isSuperAdmin()) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600">
              You don't have permission to access this area.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/app-admin')}
            className="mr-4 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div className="flex items-center">
            <PlayCircleIcon className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">AI Management</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('run-report')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'run-report'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center">
              <PlayCircleIcon className="w-5 h-5 mr-2" />
              Run Report
            </div>
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'prompts'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Prompts
            </div>
          </button>
          <button
            onClick={() => setActiveTab('creeds')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'creeds'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center">
              <BookmarkIcon className="w-5 h-5 mr-2" />
              Creed Statements
            </div>
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'reports'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center">
              <DocumentIcon className="w-5 h-5 mr-2" />
              Reports
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <ArrowPathIcon className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}
          {activeTab === 'run-report' && renderRunReportTab()}
          {activeTab === 'prompts' && renderPromptsTab()}
          {activeTab === 'creeds' && renderCreedStatementsTab()}
          {activeTab === 'reports' && renderReportsTab()}
        </div>
      </div>
    </Layout>
  );
};

export default AIManagement;
